import React, { useCallback, useMemo } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'

import { clientApi } from '@/api/client'
import { qk } from '@/api/queryKeys'
import ErrorBox from '@/components/ErrorBox'
import Topbar from '@/components/Topbar'
import ProbBar from '@/components/ProbBar'
import Delta from '@/components/Delta'
import Sparkline from '@/components/Sparkline'
import Skeleton from '@/components/Skeleton'
import { fmtProb, fmtEdge, timeAgo } from '@/utils/format'

type StatusFilter = 'all' | 'live' | 'pending' | 'resolved'
type SortKey = 'prob' | 'edge' | 'updated'

const STATUS_FILTERS: readonly StatusFilter[] = ['all', 'live', 'pending', 'resolved'] as const
const SORT_KEYS: readonly SortKey[] = ['prob', 'edge', 'updated'] as const

function itemStatus(it: any): Exclude<StatusFilter, 'all'> {
  const sqStatus = String(it?.scored_question?.status || '').toLowerCase()
  if (sqStatus === 'resolved' || sqStatus === 'invalid') return 'resolved'
  return typeof it?.state?.probability === 'number' ? 'live' : 'pending'
}

function parseStatusFilter(value: string | null): StatusFilter {
  return (STATUS_FILTERS as readonly string[]).includes(String(value)) ? (value as StatusFilter) : 'all'
}
function parseSortKey(value: string | null): SortKey {
  return (SORT_KEYS as readonly string[]).includes(String(value)) ? (value as SortKey) : 'prob'
}

export default function Watchlist() {
  const { watchlistId } = useParams()
  // Filter, sort, and search state lives in the URL so the view is
  // bookmarkable, survives page reloads, and shares cleanly via copy-
  // paste. We keep the shape minimal (s/o/q params) to avoid clutter.
  const [searchParams, setSearchParams] = useSearchParams()
  const statusFilter = parseStatusFilter(searchParams.get('s'))
  const sortKey = parseSortKey(searchParams.get('o'))
  const search = searchParams.get('q') || ''

  const updateParam = useCallback((name: 's' | 'o' | 'q', value: string | null, defaultValue: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      if (!value || value === defaultValue) next.delete(name)
      else next.set(name, value)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const setStatusFilter = (s: StatusFilter) => updateParam('s', s, 'all')
  const setSortKey = (o: SortKey) => updateParam('o', o, 'prob')
  const setSearch = (q: string) => updateParam('q', q, '')

  const detailQuery = useQuery({
    queryKey: qk.watchlists.detail(watchlistId || ''),
    queryFn: () => clientApi.watchlistDetail(watchlistId as string),
    enabled: !!watchlistId,
    staleTime: 30_000,
  })
  const data = detailQuery.data ?? null
  const error = detailQuery.error

  const rawItems = useMemo<any[]>(() => (Array.isArray(data?.items) ? data!.items : []), [data])

  // Pre-compute per-item status / counts so filter buttons can show them.
  const counts = useMemo(() => {
    const c = { all: rawItems.length, live: 0, pending: 0, resolved: 0 }
    for (const it of rawItems) c[itemStatus(it)] += 1
    return c
  }, [rawItems])

  const filteredAndSorted = useMemo(() => {
    const q = search.trim().toLowerCase()
    const filtered = rawItems.filter((it) => {
      if (statusFilter !== 'all' && itemStatus(it) !== statusFilter) return false
      if (q) {
        const hay = [
          it?.label_override,
          it?.scored_question?.title,
          it?.canonical_question?.title,
        ]
          .map((x) => String(x || '').toLowerCase())
          .join(' ')
        if (!hay.includes(q)) return false
      }
      return true
    })

    const sorted = [...filtered].sort((a, b) => {
      if (sortKey === 'prob') {
        const aProb = a?.state?.probability
        const bProb = b?.state?.probability
        const aHas = typeof aProb === 'number' ? 1 : 0
        const bHas = typeof bProb === 'number' ? 1 : 0
        if (aHas !== bHas) return bHas - aHas
        if (aHas && bHas) return (bProb as number) - (aProb as number)
        return 0
      }
      if (sortKey === 'edge') {
        const aE = typeof a?.state?.edge_live === 'number' ? Math.abs(a.state.edge_live) : -1
        const bE = typeof b?.state?.edge_live === 'number' ? Math.abs(b.state.edge_live) : -1
        return bE - aE
      }
      // 'updated'
      const aT = Date.parse(String(a?.state?.as_of || '')) || 0
      const bT = Date.parse(String(b?.state?.as_of || '')) || 0
      return bT - aT
    })
    return sorted
  }, [rawItems, statusFilter, sortKey, search])

  if (!data && !error) {
    return (
      <div className="container">
        <Topbar subtitle="Watchlist" />
        <div className="grid" style={{ marginTop: 16 }}>
          <Skeleton variant="card" count={4} />
        </div>
      </div>
    )
  }

  return (
    <div className="container">
      <Topbar
        subtitle={data?.watchlist?.name || 'Watchlist'}
        right={
          <Link className="btn" to={`/watchlists/${watchlistId}/feed`}>
            Feed
          </Link>
        }
      />

      {/* Filters / sort / search bar */}
      <div
        className="card"
        style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}
        role="toolbar"
        aria-label="Watchlist filters"
      >
        <div className="row" style={{ gap: 6, flexWrap: 'wrap' }} role="group" aria-label="Status filter">
          {(
            [
              ['all', 'All'],
              ['live', 'Live'],
              ['pending', 'Pending'],
              ['resolved', 'Resolved'],
            ] as [StatusFilter, string][]
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              className={`btn small ${statusFilter === key ? 'primary' : ''}`}
              onClick={() => setStatusFilter(key)}
              aria-pressed={statusFilter === key}
            >
              {label} ({counts[key]})
            </button>
          ))}
        </div>

        <div className="row" style={{ gap: 6, alignItems: 'center' }}>
          <label htmlFor="watchlist-sort" className="muted" style={{ fontSize: 12 }}>Sort</label>
          <select
            id="watchlist-sort"
            className="input"
            style={{ fontSize: 12, padding: '4px 8px' }}
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
          >
            <option value="prob">Probability (desc)</option>
            <option value="edge">|Edge| (desc)</option>
            <option value="updated">Recently updated</option>
          </select>
        </div>

        <div style={{ flex: 1, minWidth: 160 }}>
          <input
            type="search"
            className="input"
            placeholder="Search items by title…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Search watchlist items"
            style={{ fontSize: 13 }}
          />
        </div>

        <span className="muted" style={{ fontSize: 12 }}>
          {filteredAndSorted.length} of {rawItems.length}
        </span>
      </div>

      <div className="grid">
        {filteredAndSorted.map((it: any) => {
          const label = it.label_override || it.scored_question?.title || it.canonical_question?.title || it.item_id
          const st = it.state
          const prob = st?.probability
          const mktLive = st?.market_prob_live
          const edge = st?.edge_live
          const sparkData: number[] = st?.probability_history || []
          const probPrev = st?.probability_prev
          const delta = typeof prob === 'number' && typeof probPrev === 'number' ? prob - probPrev : null
          const status = itemStatus(it)

          return (
            <div key={it.item_id} className="item-card">
              <div className="item-card-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Link to={`/items/${it.item_id}`} className="titleLink">
                    {label}
                  </Link>
                  {it.canonical_question?.url && (
                    <a className="tinyLink" href={it.canonical_question.url} target="_blank" rel="noreferrer">
                      market
                    </a>
                  )}
                  <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {it.canonical_question?.title ? `Event: ${it.canonical_question.title}` : ''}
                    {it.canonical_question?.close_ts ? ` \u00b7 Close: ${String(it.canonical_question.close_ts).slice(0, 10)}` : ''}
                  </div>
                </div>
                {sparkData.length >= 2 && <Sparkline values={sparkData} />}
              </div>

              <div className="item-card-body">
                {typeof prob === 'number' ? (
                  <>
                    <div className="item-card-prob-row">
                      <div style={{ flex: 1 }}>
                        <ProbBar prob={prob} marketProb={mktLive} />
                      </div>
                      <Delta value={delta} />
                    </div>

                    {typeof mktLive === 'number' && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        Mkt: {fmtProb(mktLive)}
                        {typeof edge === 'number' && <> &middot; Edge: {fmtEdge(edge)}</>}
                        {st?.as_of && <> &middot; {timeAgo(st.as_of)}</>}
                      </div>
                    )}
                    {typeof mktLive !== 'number' && st?.as_of && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                        {timeAgo(st.as_of)}
                      </div>
                    )}

                    {st?.summary && (
                      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                        {String(st.summary).slice(0, 160)}
                      </div>
                    )}
                  </>
                ) : status === 'resolved' ? (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
                    Resolved.
                  </div>
                ) : (
                  <div className="muted" style={{ fontSize: 12, marginTop: 4, fontStyle: 'italic' }}>
                    Forecast pending &mdash; this question will be forecasted in the next cycle.
                  </div>
                )}
              </div>
            </div>
          )
        })}

        {filteredAndSorted.length === 0 && !error && (
          <div className="muted" style={{ fontSize: 13, padding: 12 }}>
            No items match the current filter{search ? ' / search' : ''}.
          </div>
        )}

        {error ? <ErrorBox error={error} /> : null}
      </div>
    </div>
  )
}
